/**
 * nt-quote-api.js
 * Shared helpers for every quote surface in this theme (hero form, v1 drawer,
 * v2 drawer): field validation with per-field messages, the 20MB logo cap,
 * image upload, and the "normal" lead submit + "prospect" partial-save audit
 * trail. Load this before any script that references window.NTQuoteAPI.
 *
 * Endpoint URLs are read from window.NTQuoteConfig, which layout/theme.liquidone 
 * and layout/nt-lead-quote.liquid populate from the "Quote form API" theme
 * settings (config/settings_schema.json) — so the backend URL can be changed
 * from the Shopify admin without touching any code. The literals below are
 * only a fallback for pages that somehow load this script without that
 * config block.
 */
window.NTQuoteAPI = (function () {
  'use strict';

  var config = window.NTQuoteConfig || {};
  var LEADS_API_URL = config.leadsApiUrl || 'https://forms.signmakerz.com/api/leads';
  var PROSPECTS_API_URL = config.prospectApiUrl || 'https://forms.signmakerz.com/api/prospects';
  var IMAGE_UPLOAD_URL = config.imageUploadApiUrl || 'https://imagescloudv2.codeblue.agency/api/image/upload';
  // Logo delivery has three routes, tried in order until one works:
  //   1. 'direct' — IMAGE_UPLOAD_URL, straight from the browser (normal case)
  //   2. 'proxy'  — the same request to our forms backend, which forwards it
  //                 server-to-server; reaches visitors whose ad blocker / VPN /
  //                 firewall blocks the image server's domain
  //   3. 'inline' — the file (shrunk to INLINE_MAX_BYTES) rides along with
  //                 the lead itself as images_base64, and the backend uploads
  //                 it after saving the lead
  // If even that's impossible, the lead is still submitted and names the
  // file in image_upload_failed_files so sales can ask for it.
  var FALLBACK_UPLOAD_URL = config.imageUploadFallbackUrl || LEADS_API_URL.replace(/\/leads\/?$/, '/upload/image');
  var PROXY_ATTEMPTS = 2;
  // HTTP statuses that mean "this file is not acceptable" (bad request, too
  // large, wrong type, invalid) — no other route will take it either. Any
  // other 4xx (404, 405, 401…) means the route itself is unavailable.
  var FILE_REJECTION_STATUSES = [400, 413, 415, 422];
  var INLINE_MAX_BYTES = 1024 * 1024;
  var INLINE_MAX_DIMENSION = 2000;
  // Formats a canvas can decode, so they can be shrunk to fit INLINE_MAX_BYTES.
  var SHRINKABLE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'];
  var MAX_LOGO_SIZE = 20 * 1024 * 1024; // 20MB
  var ALLOWED_LOGO_EXT = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'pdf', 'ai', 'eps', 'tiff'];
  // Backoff before each automatic retry; attempts = 1 initial + one per delay.
  // Most failures we see are flaky mobile connections, which a single quick
  // retry rarely outlasts.
  var RETRY_DELAYS_MS = [2000, 5000, 10000];
  var MAX_UPLOAD_ATTEMPTS = RETRY_DELAYS_MS.length + 1;
  // A fixed total timeout punishes big files on slow links, so instead abort
  // only when the upload stops making progress, or the server goes quiet
  // after receiving the whole body.
  var UPLOAD_STALL_MS = 30000;
  var RESPONSE_WAIT_MS = 60000;
  var UPLOAD_HARD_CAP_MS = 10 * 60 * 1000;

  // window.safeLocalStorage (layout/theme.liquid) never throws even when
  // storage is blocked/partitioned — prefer it, falling back to raw
  // localStorage only on pages/scripts that load before that wrapper exists.
  function LS() {
    return window.safeLocalStorage || localStorage;
  }

  // Clarity tracker session (assets/clarity-tracker.js) — the same
  // session_uuid sent to /api/tracker/events, so a prospect can be joined to
  // its tracked session. Falls back to the tracker's persisted session id
  // when window.Clarity hasn't loaded yet.
  function claritySessionUuid() {
    try {
      var id = window.Clarity && window.Clarity.getSessionUuid && window.Clarity.getSessionUuid();
      if (id) return id;
      var m = document.cookie.match('(?:^|; )clarity_session_uuid=([^;]*)');
      if (m) return decodeURIComponent(m[1]);
      var ss = window.safeSessionStorage || window.sessionStorage;
      return (ss && ss.getItem('clarity_session_uuid')) || LS().getItem('clarity_session_uuid') || '';
    } catch (_) {
      return '';
    }
  }

  function uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function fmtSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function getFileExt(name) {
    var m = /\.([a-z0-9]+)$/i.exec(String(name || ''));
    return m ? m[1].toLowerCase() : '';
  }

  var validators = {
    isName: function (v) {
      return (v || '').trim().length >= 2;
    },
    isEmail: function (v) {
      return /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test((v || '').trim());
    },
    isPhone: function (v) {
      var d = (v || '').replace(/\D/g, '');
      return d.length === 11 && d.charAt(0) === '1';
    },
    // Live mask, matching the signs.inc reference form: formats as the user
    // types into "+1 (205) 900-7525", capping at 10 significant digits.
    //
    // This is called with the field's CURRENT (already-masked) value on
    // every keystroke, so the "+1 " this function itself printed last time
    // is already sitting in v — and its "1" would re-enter the digit stream
    // on every call if we extracted digits first. Strip that literal prefix
    // text (not a digit-count guess) before touching the digits, so a
    // leading "1" the user actually typed is never confused with our own
    // mask decoration. A separate, length-gated check still drops a
    // genuine country-code "1" from an 11-digit paste (e.g. "12345678901"
    // pasted in one go, with no "+1 " prefix yet for the literal strip to
    // catch).
    formatPhone: function (v) {
      v = v || '';
      if (v.slice(0, 2) === '+1') v = v.slice(2);
      var raw = v.replace(/\D/g, '');
      if (raw.length > 10 && raw.charAt(0) === '1') raw = raw.slice(1);
      var d = raw.slice(0, 10);
      if (!d.length) return '';
      var out = '+1 ';
      if (d.length > 6) {
        out += '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
      } else if (d.length > 3) {
        out += '(' + d.slice(0, 3) + ') ' + d.slice(3);
      } else {
        out += '(' + d;
      }
      return out;
    },
    fieldError: function (key, value) {
      value = (value || '').trim();
      if (key === 'name') {
        if (!value) return 'Please enter your full name.';
        if (value.length < 2) return 'That name looks too short.';
        return '';
      }
      if (key === 'email') {
        if (!value) return 'Please enter your email address.';
        if (!validators.isEmail(value)) return 'Double-check that email — looks slightly off.';
        return '';
      }
      if (key === 'phone') {
        if (!value) return 'Please enter your phone number.';
        if (!validators.isPhone(value)) return 'Please enter a valid 10-digit phone number.';
        return '';
      }
      return '';
    },
    // Required logo field: missing file, or file over the 20MB cap.
    fileError: function (file) {
      if (!file) return 'Please upload your logo so we can build your mockup.';
      if (file.size > MAX_LOGO_SIZE) {
        return '"' + file.name + '" is ' + fmtSize(file.size) + ' — max allowed is 20MB.';
      }
      return '';
    },
  };

  function uploadImages(files, meta) {
    var list = files ? Array.prototype.slice.call(files) : [];
    if (!list.length) return Promise.resolve([]);
    var fd = new FormData();
    fd.append('name', (meta && meta.name) || 'lead');
    fd.append('page_url', window.location.href);
    fd.append('uuid', (meta && meta.uuid) || uuid());
    list.forEach(function (f) {
      fd.append('images[]', f);
    });
    return fetch(IMAGE_UPLOAD_URL, { method: 'POST', body: fd }).then(function (res) {
      if (!res.ok) throw new Error('Image upload failed with status ' + res.status);
      return res.json().then(function (data) {
        return (data && data.imageUrls) || [];
      });
    });
  }

  function describeUploadFailure(kind, xhr) {
    if (kind === 'timeout') return 'Upload timed out — check your internet connection and try again.';
    if (kind === 'network') return 'Network error — check your internet connection and try again.';
    var status = xhr && xhr.status;
    var serverMsg = '';
    try {
      var data = JSON.parse(xhr.responseText);
      serverMsg = (data && (data.error || data.message)) || '';
    } catch (e) {
      // response wasn't JSON — fall back to a status-based reason below
    }
    if (serverMsg) return serverMsg;
    if (status === 413) return 'File is too large for the server to accept.';
    if (status === 415 || status === 400) return 'Unsupported file format.';
    if (status >= 500) return 'Server error — please try again in a moment.';
    if (status) return 'Upload failed (server responded with status ' + status + ').';
    return 'Upload failed. Please try again.';
  }

  // Transient failures worth retrying automatically; a 4xx rejection or a
  // file the server refused will fail the same way every time.
  function isRetryable(kind, status) {
    if (kind === 'network' || kind === 'timeout' || kind === 'parse') return true;
    return !status || status === 408 || status === 429 || status >= 500;
  }

  // Upload failures never reach the upload server's logs when the request
  // dies in transit (blocked domain, dropped connection, nginx error page
  // without CORS headers), so report them from the browser. Pushed to GTM's
  // dataLayer as "nt_logo_upload_failed"; progress_pct / body_sent tell the
  // causes apart: 0% = blocked before sending, partial = connection dropped,
  // body sent = the server failed to answer.
  function reportUploadEvent(eventName, entries, info) {
    try {
      var conn = navigator.connection || {};
      var totalBytes = entries.reduce(function (sum, e) { return sum + (e.file.size || 0); }, 0);
      var payload = {
        event: eventName,
        upload_route: info.route || '',
        upload_error_kind: info.kind || '',
        upload_http_status: info.status || 0,
        upload_attempt: info.attempt,
        upload_will_retry: !!info.willRetry,
        upload_progress_pct: info.progress || 0,
        upload_body_sent: !!info.bodySent,
        upload_elapsed_ms: info.elapsed || 0,
        upload_file_count: entries.length,
        upload_file_kb: Math.round(totalBytes / 1024),
        upload_file_ext: entries.map(function (e) { return getFileExt(e.file.name); }).join(','),
        upload_online: navigator.onLine !== false,
        upload_connection: conn.effectiveType || '',
        upload_page: window.location.pathname,
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (eventName === 'nt_logo_upload_failed') console.warn('Logo upload failed', payload);
    } catch (e) {
      // reporting must never break the upload flow
    }
  }

  // Browsers never expose why a request failed at the network level (an ad
  // blocker, VPN, firewall or DNS filter all look like "status 0" to JS), so
  // after a network error we probe two URLs and infer the cause: can the
  // browser reach this site at all, and can it reach the upload server?
  var PROBE_TIMEOUT_MS = 6000;

  function probe(url, init) {
    return new Promise(function (resolve) {
      var done = false;
      function settle(ok) {
        if (done) return;
        done = true;
        resolve(ok);
      }
      setTimeout(function () { settle(false); }, PROBE_TIMEOUT_MS);
      try {
        // Any HTTP response (even 404, or an opaque no-cors one) proves the
        // host is reachable; only a blocked/failed connection rejects.
        fetch(url, init).then(function () { settle(true); }, function () { settle(false); });
      } catch (e) {
        settle(false);
      }
    });
  }

  function originOf(url) {
    var m = /^(https?:\/\/[^/]+)/i.exec(url);
    return m ? m[1] : '';
  }

  var NETWORK_CAUSES = {
    offline: {
      retryable: true,
      reason: 'You appear to be offline — check your internet connection and try again.',
    },
    blocked: {
      retryable: false,
      reason: 'Your browser or network is blocking our upload server. Pause any ad blocker, privacy extension or VPN for this site, or try a different browser or network.',
    },
    refused: {
      retryable: true,
      reason: 'Your browser stopped the upload before it started — usually an ad blocker, privacy extension or security software. Pause it for this site, or try a different browser.',
    },
    dropped: {
      retryable: true,
      reason: 'Your connection dropped during the upload — please try again, ideally on a stronger connection.',
    },
    server: {
      retryable: true,
      reason: 'Our upload server didn’t respond properly — please try again in a moment.',
    },
  };

  // Resolves to one of the NETWORK_CAUSES keys.
  function diagnoseNetworkFailure(uploadUrl, bodySent, progressPct) {
    if (navigator.onLine === false) return Promise.resolve('offline');
    var bust = '_nt_probe=' + Date.now();
    var origin = originOf(uploadUrl);
    return Promise.all([
      probe('/robots.txt?' + bust, { cache: 'no-store', credentials: 'omit' }),
      origin ? probe(origin + '/?' + bust, { mode: 'no-cors', cache: 'no-store', credentials: 'omit' }) : Promise.resolve(true),
    ]).then(function (r) {
      var siteOk = r[0];
      var uploadHostOk = r[1];
      if (!siteOk) return 'offline';
      if (!uploadHostOk) return 'blocked';
      if (bodySent) return 'server';
      return progressPct > 0 ? 'dropped' : 'refused';
    });
  }

  // Copies a picked file into memory so the upload no longer depends on the
  // device's original. Mobile browsers hand the page photos / cloud files
  // through temporary handles tied to the <input>, and those can become
  // unreadable mid-upload (the browser then reports it as a generic network
  // error, and every retry fails the same way). The in-memory copy keeps
  // name/size/lastModified, so duplicate detection is unaffected.
  function snapshotFile(f) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        var copy;
        try {
          copy = new File([reader.result], f.name, { type: f.type, lastModified: f.lastModified });
        } catch (e) {
          // very old Safari: no File constructor
          copy = new Blob([reader.result], { type: f.type });
          copy.name = f.name;
          copy.lastModified = f.lastModified;
        }
        resolve(copy);
      };
      reader.onerror = function () { reject(reader.error); };
      reader.readAsArrayBuffer(f);
    });
  }

  function blobToBase64(blob) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        var s = String(reader.result || '');
        resolve(s.slice(s.indexOf(',') + 1));
      };
      reader.onerror = function () { reject(reader.error); };
      reader.readAsDataURL(blob);
    });
  }

  function loadImage(blob) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(blob);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
      img.src = url;
    });
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise(function (resolve) {
      if (!canvas.toBlob) { resolve(null); return; }
      canvas.toBlob(function (b) { resolve(b); }, type, quality);
    });
  }

  // Re-encodes an image at decreasing size/quality until it fits maxBytes.
  // WebP keeps logo transparency; browsers that can't encode WebP hand back
  // PNG instead, so fall back to JPEG on a white background.
  function shrinkImage(file, maxBytes) {
    return loadImage(file).then(function (img) {
      var w0 = img.naturalWidth || img.width;
      var h0 = img.naturalHeight || img.height;
      if (!w0 || !h0) return null;
      var steps = [];
      [1, 0.7, 0.5].forEach(function (scale) {
        [0.85, 0.7, 0.55].forEach(function (q) { steps.push({ scale: scale, q: q }); });
      });
      var base = Math.min(1, INLINE_MAX_DIMENSION / Math.max(w0, h0));

      function attempt(i, type) {
        if (i >= steps.length) return Promise.resolve(null);
        var s = steps[i];
        var canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(w0 * base * s.scale));
        canvas.height = Math.max(1, Math.round(h0 * base * s.scale));
        var ctx = canvas.getContext('2d');
        if (type === 'image/jpeg') {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        return canvasToBlob(canvas, type, s.q).then(function (blob) {
          if (!blob) return null;
          if (type === 'image/webp' && blob.type !== 'image/webp') return attempt(i, 'image/jpeg');
          return blob.size <= maxBytes ? blob : attempt(i + 1, type);
        });
      }

      return attempt(0, 'image/webp');
    });
  }

  // Resolves to { name, type, data } (data = base64) for images_base64, or
  // null when the file can't be made small enough (e.g. a 5MB PDF).
  function toInlineFile(file) {
    var ext = getFileExt(file.name);
    var small = file.size <= INLINE_MAX_BYTES;
    var source = small
      ? Promise.resolve(file)
      : SHRINKABLE_EXT.indexOf(ext) !== -1
        ? shrinkImage(file, INLINE_MAX_BYTES).catch(function () { return null; })
        : Promise.resolve(null);
    return source.then(function (blob) {
      if (!blob || blob.size > INLINE_MAX_BYTES) return null;
      var name = blob === file
        ? file.name
        : file.name.replace(/\.[^.]+$/, '') + (blob.type === 'image/webp' ? '.webp' : '.jpg');
      return blobToBase64(blob).then(function (data) {
        return { name: name, type: blob.type || file.type || '', data: data };
      });
    });
  }

  /**
   * Multi-file logo/artwork manager: validates + eagerly uploads each file
   * the instant it's selected/dropped (not at final submit time), tracks
   * per-file progress/status, retries a failed upload automatically with
   * backoff (see RETRY_DELAYS_MS),
   * and lets the caller remove one file or clear all of them. The caller
   * owns rendering — pass onChange to re-render your file list whenever
   * the underlying state changes.
   *
   * Entry shape: { id, file, status: 'pending'|'uploading'|'done'|'inline'|'error'|'invalid',
   *                url, reason, attempts, progress, retryScheduled, snapshotted,
   *                route: 'direct'|'proxy', inline, undeliverable }
   * retryScheduled is true while an 'error' entry waits for its next
   * automatic attempt — render it as "retrying", not as a final failure.
   * 'inline' means both upload routes failed and the file will be sent with
   * the lead (leadImageFields()); undeliverable marks an 'error' entry that
   * no route could deliver — the visitor can still submit (hasLogo()).
   * Use NTQuoteAPI.entryStatus() / fileHint() to render these consistently.
   */
  function createFileManager(opts) {
    opts = opts || {};
    var maxSize = opts.maxSize || MAX_LOGO_SIZE;
    var allowedExt = opts.allowedExt || ALLOWED_LOGO_EXT;
    var managerUuid = opts.uuid || uuid();
    var getName = typeof opts.name === 'function' ? opts.name : function () { return opts.name || 'lead'; };
    var onChange = opts.onChange || function () { };
    var files = [];
    var idCounter = 0;

    function notify() {
      onChange(files.slice());
    }

    function findIndex(id) {
      for (var i = 0; i < files.length; i++) if (files[i].id === id) return i;
      return -1;
    }

    function isTracked(e) {
      return findIndex(e.id) !== -1;
    }

    function scheduleRetry(entries, maxAttempts) {
      // Skip files the visitor removed (or cleared) while this attempt ran.
      var due = entries.filter(function (e) {
        return isTracked(e) && e.status === 'error' && e.attempts < maxAttempts;
      });
      if (!due.length) return false;
      var delay = RETRY_DELAYS_MS[Math.min(due[0].attempts, RETRY_DELAYS_MS.length) - 1];
      var timer = setTimeout(function () {
        var still = due.filter(function (e) { return isTracked(e) && e.retryTimer === timer; });
        still.forEach(function (e) { e.retryTimer = null; });
        uploadEntries(still);
      }, delay);
      due.forEach(function (e) {
        e.retryScheduled = true;
        e.retryTimer = timer;
        // Keep the cause ("Your connection dropped…") but swap its advice
        // for the retry status: messages are "<cause> — <advice>".
        // The backup route retries silently, like the switch onto it.
        if (e.route === 'proxy') {
          e.status = 'uploading';
          e.progress = 0;
          e.reason = '';
          return;
        }
        var cause = (e.reason || 'Connection problem').split(' — ')[0].replace(/\.$/, '');
        e.reason = cause + ' — retrying automatically (attempt ' + (e.attempts + 1) + ' of ' + maxAttempts + ')…';
      });
      return true;
    }

    // Route 1 → 2: retry right away through our own domain.
    function switchToProxy(entries) {
      var due = entries.filter(isTracked);
      if (!due.length) return;
      var timer = setTimeout(function () {
        var still = due.filter(function (e) { return isTracked(e) && e.retryTimer === timer; });
        still.forEach(function (e) { e.retryTimer = null; });
        uploadEntries(still);
      }, 300);
      due.forEach(function (e) {
        e.route = 'proxy';
        e.attempts = 0;
        e.retryScheduled = true;
        e.retryTimer = timer;
        // Switching routes is silent: keep showing a normal upload.
        e.status = 'uploading';
        e.progress = 0;
        e.reason = '';
      });
    }

    // Route 2 → 3: prepare each file to travel with the lead itself.
    function fallBackToInline(entries) {
      var due = entries.filter(isTracked);
      if (!due.length) return;
      due.forEach(function (e) {
        e.status = 'uploading';
        e.progress = 0;
        e.retryScheduled = false;
      });
      notify();
      Promise.all(due.map(function (e) {
        return toInlineFile(e.file).catch(function () { return null; }).then(function (inline) {
          if (!isTracked(e)) return;
          if (inline) {
            e.status = 'inline';
            e.inline = inline;
            e.reason = 'We’ll send this file along with your request.';
          } else {
            e.status = 'error';
            e.undeliverable = true;
            e.reason = 'We couldn’t upload this file. You can still submit your request — just email the file to us afterwards.';
          }
          reportUploadEvent(inline ? 'nt_logo_upload_inline' : 'nt_logo_upload_failed', [e], {
            route: 'inline',
            kind: inline ? 'inline' : 'undeliverable',
            attempt: e.attempts,
          });
        });
      })).then(notify);
    }

    function cancelRetry(e) {
      if (e.retryTimer) clearTimeout(e.retryTimer);
      e.retryTimer = null;
      e.retryScheduled = false;
    }

    function uploadEntries(entries) {
      if (!entries.length) return;
      var route = entries[0].route || 'direct';
      var uploadUrl = route === 'proxy' ? FALLBACK_UPLOAD_URL : IMAGE_UPLOAD_URL;
      entries.forEach(function (e) {
        cancelRetry(e);
        e.status = 'uploading';
        e.attempts = (e.attempts || 0) + 1;
        e.progress = 0;
      });
      notify();

      var fd = new FormData();
      fd.append('name', getName());
      fd.append('page_url', window.location.href);
      fd.append('uuid', managerUuid);
      entries.forEach(function (e) {
        fd.append('images[]', e.file, e.file.name);
      });

      var xhr = new XMLHttpRequest();
      var startedAt = Date.now();
      var lastActivityAt = startedAt;
      var bodySent = false;
      var progressPct = 0;
      var settled = false;
      var abortKind = '';

      // Watchdog in place of xhr.timeout: see UPLOAD_STALL_MS above.
      var watchdog = setInterval(function () {
        var now = Date.now();
        var idle = now - lastActivityAt;
        if ((!bodySent && idle > UPLOAD_STALL_MS) || (bodySent && idle > RESPONSE_WAIT_MS) || now - startedAt > UPLOAD_HARD_CAP_MS) {
          abortKind = 'timeout';
          xhr.abort();
        }
      }, 5000);

      function finish() {
        if (settled) return false;
        settled = true;
        clearInterval(watchdog);
        return true;
      }

      function report(eventName, kind, status, willRetry, list) {
        reportUploadEvent(eventName, list || entries, {
          route: route,
          kind: kind,
          status: status,
          attempt: entries[0].attempts,
          willRetry: willRetry,
          progress: progressPct,
          bodySent: bodySent,
          elapsed: Date.now() - startedAt,
        });
      }

      function fail(kind, failedXhr) {
        if (!finish()) return;
        var status = failedXhr ? failedXhr.status : 0;
        if (kind !== 'network') {
          settleFailure(kind, status, describeUploadFailure(kind, failedXhr), isRetryable(kind, status));
          return;
        }
        // Entries stay 'uploading' while we diagnose (a few seconds at most),
        // so the form keeps waiting instead of flashing a vague error.
        diagnoseNetworkFailure(uploadUrl, bodySent, progressPct).then(function (cause) {
          var c = NETWORK_CAUSES[cause];
          settleFailure('network:' + cause, 0, c.reason, c.retryable, cause);
        }, function () {
          settleFailure('network', 0, describeUploadFailure('network'), true, '');
        });
      }

      // Decides what happens after a failed attempt:
      //   - a rejection of the file itself (FILE_REJECTION_STATUSES) stops —
      //     every route would refuse it
      //   - direct route: blocked/refused/server problems (incl. other 4xx,
      //     e.g. a missing route) switch to the proxy at once; connection
      //     trouble retries with backoff first
      //   - proxy route: one retry, then the file goes inline with the lead —
      //     also when the proxy itself is unavailable (404/405 before it's
      //     deployed, or a misconfigured server)
      function settleFailure(kind, status, reason, retryable, cause) {
        entries.forEach(function (e) {
          e.status = 'error';
          e.reason = reason;
        });
        var rejected = !retryable && !cause && FILE_REJECTION_STATUSES.indexOf(status) !== -1;
        var next = 'stop';
        if (!rejected && route === 'direct') {
          var switchNow = cause === 'blocked' || cause === 'refused' || cause === 'server' || kind === 'server' || kind === 'parse';
          next = !switchNow && scheduleRetry(entries, MAX_UPLOAD_ATTEMPTS) ? 'retry' : 'proxy';
        } else if (!rejected) {
          next = retryable && scheduleRetry(entries, PROXY_ATTEMPTS) ? 'retry' : 'inline';
        }
        report('nt_logo_upload_failed', kind, status, next !== 'stop');
        if (next === 'proxy') switchToProxy(entries);
        if (next === 'inline') fallBackToInline(entries);
        notify();
      }

      xhr.upload.onprogress = function (ev) {
        lastActivityAt = Date.now();
        if (!ev.lengthComputable) return;
        progressPct = Math.round((ev.loaded / ev.total) * 100);
        entries.forEach(function (e) { e.progress = progressPct; });
        notify();
      };
      xhr.upload.onload = function () {
        bodySent = true;
        progressPct = 100;
        lastActivityAt = Date.now();
      };
      xhr.onload = function () {
        if (xhr.status < 200 || xhr.status >= 300) {
          fail('server', xhr);
          return;
        }
        var urls = [];
        try {
          var data = JSON.parse(xhr.responseText);
          urls = (data && data.imageUrls) || [];
        } catch (e) {
          fail('parse', xhr);
          return;
        }
        if (!finish()) return;
        var rejected = [];
        entries.forEach(function (e, i) {
          if (urls[i]) {
            e.status = 'done';
            e.url = urls[i];
            e.reason = '';
          } else {
            e.status = 'error';
            e.reason = 'Rejected by the server — check the file format and try again.';
            rejected.push(e);
          }
        });
        if (rejected.length) report('nt_logo_upload_failed', 'rejected', xhr.status, false, rejected);
        else if (entries[0].attempts > 1 || route !== 'direct') report('nt_logo_upload_recovered', '', xhr.status, false);
        notify();
      };
      xhr.onerror = function () { fail('network'); };
      xhr.onabort = function () { fail(abortKind || 'network'); };
      xhr.open('POST', uploadUrl, true);
      xhr.send(fd);
    }

    // Snapshots any not-yet-copied entries (see snapshotFile), then uploads
    // the ones that could be read. Entries show as 'uploading' meanwhile so
    // the UI and waitForUploads() treat the copy step as part of the upload.
    function prepareAndUpload(entries) {
      entries.forEach(function (e) {
        cancelRetry(e);
        e.status = 'uploading';
        e.progress = 0;
        e.reason = '';
      });
      notify();
      Promise.all(entries.map(function (e) {
        if (e.snapshotted) return Promise.resolve(e);
        return snapshotFile(e.file).then(function (copy) {
          e.file = copy;
          e.snapshotted = true;
          return e;
        }, function (err) {
          e.status = 'error';
          // Not the visitor's fault and may keep failing (cloud placeholder
          // files) — let them re-pick it, or submit anyway and email it.
          e.undeliverable = true;
          e.reason = 'We couldn’t read this file from your device. Choose it again (if it’s in iCloud, Google Drive or OneDrive, download it first), or submit anyway and email it to us.';
          reportUploadEvent('nt_logo_upload_failed', [e], {
            kind: 'read:' + ((err && err.name) || 'unknown'),
            attempt: e.attempts,
          });
          return null;
        });
      })).then(function (results) {
        var ready = results.filter(function (e) { return e && isTracked(e); });
        notify();
        uploadEntries(ready);
      });
    }

    // Adds files immediately and kicks off their upload right away — the
    // whole point being the upload happens as soon as the file is picked,
    // not deferred until the lead is submitted.
    function addFiles(fileList) {
      var incoming = Array.prototype.slice.call(fileList || []);
      var accepted = [];
      incoming.forEach(function (f) {
        var dup = files.some(function (e) {
          return e.file.name === f.name && e.file.size === f.size && e.file.lastModified === f.lastModified;
        });
        if (dup) return;
        var entry = { id: 'f' + idCounter++, file: f, status: 'pending', url: '', reason: '', attempts: 0, progress: 0, route: 'direct' };
        var ext = getFileExt(f.name);
        if (!allowedExt.includes(ext)) {
          entry.status = 'invalid';
          entry.reason = (ext ? '"' + ext.toUpperCase() + '"' : 'This file type') + ' isn’t supported.';
        } else if (f.size > maxSize) {
          entry.status = 'invalid';
          entry.reason = '"' + f.name + '" is ' + fmtSize(f.size) + ' — max allowed is ' + fmtSize(maxSize) + '.';
        } else {
          accepted.push(entry);
        }
        files.push(entry);
      });
      notify();
      if (accepted.length) prepareAndUpload(accepted);
      return files.slice();
    }

    // Manual retry starts over on the direct route with fresh retries.
    function retry(id) {
      var entry = files[findIndex(id)];
      if (!entry || entry.status === 'uploading') return;
      entry.attempts = 0;
      entry.route = 'direct';
      entry.inline = null;
      entry.undeliverable = false;
      prepareAndUpload([entry]);
    }

    function remove(id) {
      var i = findIndex(id);
      if (i === -1) return;
      cancelRetry(files[i]);
      files.splice(i, 1);
      notify();
    }

    function clear() {
      files.forEach(cancelRetry);
      files = [];
      notify();
    }

    // A file waiting out a retry backoff still counts as uploading, so
    // waitForUploads() doesn't let the form submit without it mid-retry.
    function isUploading() {
      return files.some(function (e) { return e.status === 'uploading' || e.retryScheduled; });
    }

    function hasDone() {
      return files.some(function (e) { return e.status === 'done'; });
    }

    function urls() {
      return files.filter(function (e) { return e.status === 'done'; }).map(function (e) { return e.url; });
    }

    // The logo requirement is met once every route has had its chance:
    // uploaded, travelling with the lead, or undeliverable (not the
    // visitor's fault — the lead names the file so sales can ask for it).
    function hasLogo() {
      return files.some(function (e) {
        return e.status === 'done' || e.status === 'inline' || (e.status === 'error' && e.undeliverable);
      });
    }

    // Image fields for the /api/leads payload (not for prospect syncs).
    function leadImageFields() {
      var fields = { images: urls() };
      var inline = files.filter(function (e) { return e.status === 'inline' && e.inline; })
        .map(function (e) { return e.inline; });
      var failed = files.filter(function (e) { return e.status === 'error' && e.undeliverable; })
        .map(function (e) { return e.file.name; });
      if (inline.length) fields.images_base64 = inline;
      if (failed.length) fields.image_upload_failed_files = failed;
      return fields;
    }

    function waitForUploads() {
      return new Promise(function (resolve) {
        if (!isUploading()) {
          resolve();
          return;
        }
        var iv = setInterval(function () {
          if (!isUploading()) {
            clearInterval(iv);
            resolve();
          }
        }, 200);
      });
    }

    return {
      uuid: managerUuid,
      addFiles: addFiles,
      retry: retry,
      remove: remove,
      clear: clear,
      isUploading: isUploading,
      hasDone: hasDone,
      hasLogo: hasLogo,
      urls: urls,
      leadImageFields: leadImageFields,
      waitForUploads: waitForUploads,
      getFiles: function () { return files.slice(); },
    };
  }

  // Status line for one file row: { text, cls ('ok'|'err'|''), canRetry }.
  function entryStatus(entry) {
    switch (entry.status) {
      case 'uploading':
        return { text: 'Uploading' + (entry.progress ? ' ' + entry.progress + '%' : '…'), cls: '', canRetry: false };
      // Each route reads as success to the visitor but has its own label, so
      // the team can tell in testing / session recordings how the file got
      // through: "Ready" = direct, "Uploaded" = via our proxy (the image
      // server was unreachable from the browser), "File ready" = sent with
      // the lead (both upload routes failed).
      case 'done':
        return { text: entry.route === 'proxy' ? 'Uploaded' : 'Ready', cls: 'ok', canRetry: false };
      case 'inline':
        return { text: 'File ready', cls: 'ok', canRetry: false };
      case 'invalid':
        return { text: entry.reason || 'Unsupported file.', cls: 'err', canRetry: false };
      case 'error':
        return { text: entry.reason || 'Upload failed.', cls: 'err', canRetry: true };
      default:
        return { text: 'Pending', cls: '', canRetry: false };
    }
  }

  // Hint under the upload box: { text, isError } — text is null when the
  // form should show its own default hint.
  function fileHint(files) {
    var some = function (fn) { return files.some(fn); };
    if (some(function (e) { return e.status === 'uploading'; })) return { text: 'Uploading…', isError: false };
    if (some(function (e) { return e.status === 'error' && e.retryScheduled; })) return { text: 'Retrying failed upload…', isError: false };
    var failed = files.filter(function (e) { return e.status === 'error'; });
    if (failed.length && failed.every(function (e) { return e.undeliverable; })) {
      return { text: 'Couldn’t upload — you can still submit and email us the file.', isError: true };
    }
    if (failed.length) return { text: 'Upload failed — tap Retry, or try a different connection.', isError: true };
    if (some(function (e) { return e.status === 'invalid'; })) return { text: null, isError: true };
    return { text: null, isError: false };
  }

  // Plain one-shot lead submit — always POSTs. Kept for callers that only
  // ever create a lead once per uuid (e.g. the quote drawers).
  function submitLead(payload) {
    return fetch(LEADS_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) throw new Error('Lead submit failed with status ' + res.status);
      return res
        .json()
        .catch(function () {
          return {};
        });
    });
  }

  /**
   * Lead identity that follows the create-then-update REST pattern: the
   * first successful submit() POSTs to /leads; any submit() after that
   * (e.g. the visitor amends details and re-submits) PATCHes /leads/{uuid}
   * instead of creating a duplicate lead. A submit that fails outright
   * (network error, non-2xx) does NOT flip the created flag, so the next
   * attempt still POSTs.
   */
  function createLeadTracker(existingUuid) {
    var leadUuid = existingUuid || uuid();
    var created = false;

    function submit(payload) {
      var tracking = {
        first_click: LS().getItem('first_click') || '',
        first_visit: LS().getItem('first_visit') || '',
        visit_count: LS().getItem('visit_count') || '',
        event_source: LS().getItem('event_keys_data') || '',
        location: LS().getItem('location') || LS().getItem('locationxh') || '',
        record_source: 'Landing Page 1',
        lead_source: sessionStorage.getItem('currentURL') || '',
        tracking_metadata: (typeof window.getLeadTrackingData === 'function') ? window.getLeadTrackingData() : {},
        ProspectAirtableID: LS().getItem('ProspectAirtableID') || ''
      };
      payload = Object.assign({}, tracking, payload, { uuid: leadUuid });
      var isUpdate = created;
      var url = isUpdate ? LEADS_API_URL + '/' + encodeURIComponent(leadUuid) : LEADS_API_URL;
      var method = isUpdate ? 'PATCH' : 'POST';

      return fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then(function (res) {
        if (!res.ok) throw new Error('Lead ' + (isUpdate ? 'update' : 'submit') + ' failed with status ' + res.status);
        created = true;
        return res
          .json()
          .catch(function () {
            return {};
          });
      });
    }

    return { submit: submit, uuid: leadUuid };
  }

  // One prospect identity per page load — intentionally not persisted, so a
  // refresh or reopened tab starts a brand-new prospect rather than resuming
  // a stale one. The first sync() POSTs to /prospects to create the record;
  // every sync() after that PATCHes /prospects/{uuid} so the partial-save
  // audit trail updates the same record instead of creating a new one each
  // time. Requests are chained so the create always lands before any update
  // is sent for the same uuid.
  function createProspectTracker() {
    LS().removeItem('ProspectAirtableID');
    var prospectUuid = uuid();
    var created = false;
    var finalized = false;
    var lastPayloadStr = null;
    var chain = Promise.resolve();

    function hasData(payload) {
      var filled = ['name', 'phone', 'email', 'details', 'estimated_size', 'sign_placement'].some(function (k) {
        return payload[k] && String(payload[k]).trim();
      });
      return filled || (payload.images && payload.images.length > 0);
    }

    function sync(partialPayload) {
      if (finalized) return;
      var tracking = {
        first_click: LS().getItem('first_click') || '',
        first_visit: LS().getItem('first_visit') || '',
        visit_count: LS().getItem('visit_count') || '',
        event_source: LS().getItem('event_keys_data') || '',
        location: LS().getItem('location') || LS().getItem('locationxh') || '',
        record_source: 'Landing Page 1',
        lead_source: sessionStorage.getItem('currentURL') || '',
        tracking_metadata: (typeof window.getLeadTrackingData === 'function') ? window.getLeadTrackingData() : {},
        ProspectAirtableID: LS().getItem('ProspectAirtableID') || ''
      };
      var payload = Object.assign({ uuid: prospectUuid, page_url: window.location.href, session_uuid: claritySessionUuid() }, tracking, partialPayload);
      if (!hasData(payload)) return;
      var str = JSON.stringify(payload);
      if (str === lastPayloadStr) return;
      lastPayloadStr = str;

      var isCreate = !created;
      var url = isCreate ? PROSPECTS_API_URL : PROSPECTS_API_URL + '/' + encodeURIComponent(prospectUuid);
      var method = isCreate ? 'POST' : 'PATCH';
      // Optimistic: mark created before the request settles so a second
      // sync fired right after this one (e.g. blur + pagehide) PATCHes
      // instead of racing another POST.
      if (isCreate) created = true;

      chain = chain
        .then(function () {
          return fetch(url, {
            method: method,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: str,
            keepalive: true,
          });
        })
        .then(function (res) {
          if (!res.ok) {
            if (isCreate) created = false;
            return;
          }
          return res.json().then(function (data) {
            var airtableId = data && (data.airtableRecordId || (data.data && data.data.airtable_record_id));
            if (airtableId) {
              LS().setItem('ProspectAirtableID', airtableId);
            }
          }).catch(function () { });
        })
        .catch(function (e) {
          if (isCreate) created = false;
          console.warn('Prospect sync failed:', e && e.message);
        });
    }

    function finalize() {
      finalized = true;
    }

    function getChain() {
      return chain;
    }

    return { sync: sync, finalize: finalize, uuid: prospectUuid, getChain: getChain };
  }


  return {
    LEADS_API_URL: LEADS_API_URL,
    PROSPECTS_API_URL: PROSPECTS_API_URL,
    IMAGE_UPLOAD_URL: IMAGE_UPLOAD_URL,
    MAX_LOGO_SIZE: MAX_LOGO_SIZE,
    uuid: uuid,
    fmtSize: fmtSize,
    validators: validators,
    uploadImages: uploadImages,
    createFileManager: createFileManager,
    entryStatus: entryStatus,
    fileHint: fileHint,
    submitLead: submitLead,
    createLeadTracker: createLeadTracker,
    createProspectTracker: createProspectTracker,
  };
})();
